/**
 * Client-side DOCX text extraction.
 *
 * Validates the ZIP archive at the binary level (archive size, entry count,
 * document manifest, per-entry size caps, compression-ratio bomb checks)
 * then delegates text extraction to `officeparser`. officeparser is loaded
 * via dynamic import() to code-split it out of the main bundle.
 *
 * The module has no client- or server-only imports so it can be consumed from
 * both browser uploads and server-side parsers if needed.
 */
import type { OfficeContentNode } from "officeparser";
import { DOCX_NO_TEXT_ERROR } from "@/constants/deckParse";

// ---------------------------------------------------------------------------
// Safety constants — applied BEFORE officeparser touches the buffer
// ---------------------------------------------------------------------------

const MAX_ARCHIVE_ENTRIES = 5_000;
const MAX_DOCUMENT_XML_BYTES = 10 * 1024 * 1024;
const MAX_ARCHIVE_DECLARED_UNCOMPRESSED_BYTES = 128 * 1024 * 1024;
const UNCOMPRESSED_BYTES_SLACK = 1024 * 1024;
const MAX_EXTRACTED_TEXT_CHARS = 200_000;
const MAX_COMPRESSION_RATIO = 200;
const MAX_ARCHIVE_BYTES = 50 * 1024 * 1024;
const ZIP_END_OF_CENTRAL_DIRECTORY_SIGNATURE = 0x06054b50;
const ZIP_END_OF_CENTRAL_DIRECTORY_MIN_BYTES = 22;
const ZIP_MAX_COMMENT_BYTES = 65_535;
const ZIP_CENTRAL_DIRECTORY_SIGNATURE = 0x02014b50;
const ZIP_CENTRAL_DIRECTORY_HEADER_BYTES = 46;

const DOCX_MIME_TYPE =
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document";

// ---------------------------------------------------------------------------
// Text sanitization
// ---------------------------------------------------------------------------

export function sanitizeDocxText(text: string): string {
  let sanitized = text.replace(/\0/g, "");
  sanitized = sanitized.replace(/[\u200B-\u200D\uFEFF]/g, "");
  sanitized = sanitized.replace(/[\x01-\x08\x0B-\x0C\x0E-\x1F]/g, "");

  try {
    const encoder = new TextEncoder();
    const decoder = new TextDecoder("utf-8", { fatal: false });
    sanitized = decoder.decode(encoder.encode(sanitized));
  } catch { /* benign: TextEncoder/TextDecoder unavailable in edge runtimes */
    sanitized = sanitized.replace(/[\x00-\x08\x0B-\x0C\x0E-\x1F]/g, "");
  }

  return sanitized
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .replace(/[ \t]{2,}/g, " ")
    .trim();
}

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export interface DocxTextExtraction {
  text: string;
}

// ---------------------------------------------------------------------------
// Binary archive safety validation
// ---------------------------------------------------------------------------

interface ArchiveSafetyResult {
  bytes: Uint8Array;
  totalDeclaredUncompressedBytes: number;
}

function assertArchiveSafety(data: ArrayBuffer | Uint8Array): ArchiveSafetyResult {
  const bytes =
    data instanceof Uint8Array
      ? new Uint8Array(data.buffer, data.byteOffset, data.byteLength)
      : new Uint8Array(data);
  if (bytes.byteLength > MAX_ARCHIVE_BYTES) {
    throw new Error("DOCX archive exceeds the upload extraction limit");
  }
  if (bytes.byteLength < ZIP_END_OF_CENTRAL_DIRECTORY_MIN_BYTES) {
    throw new Error("Uploaded file is not a valid DOCX archive");
  }

  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const earliestOffset = Math.max(
    0,
    bytes.byteLength -
      ZIP_END_OF_CENTRAL_DIRECTORY_MIN_BYTES -
      ZIP_MAX_COMMENT_BYTES,
  );
  let recordOffset = -1;
  for (
    let offset = bytes.byteLength - ZIP_END_OF_CENTRAL_DIRECTORY_MIN_BYTES;
    offset >= earliestOffset;
    offset -= 1
  ) {
    if (
      view.getUint32(offset, true) ===
      ZIP_END_OF_CENTRAL_DIRECTORY_SIGNATURE
    ) {
      recordOffset = offset;
      break;
    }
  }
  if (recordOffset < 0) {
    throw new Error("Uploaded file is not a valid DOCX archive");
  }

  const diskNumber = view.getUint16(recordOffset + 4, true);
  const centralDirectoryDisk = view.getUint16(recordOffset + 6, true);
  const totalEntries = view.getUint16(recordOffset + 10, true);
  const centralDirectoryBytes = view.getUint32(recordOffset + 12, true);
  const centralDirectoryOffset = view.getUint32(recordOffset + 16, true);
  const commentBytes = view.getUint16(recordOffset + 20, true);
  if (
    diskNumber !== 0 ||
    centralDirectoryDisk !== 0 ||
    totalEntries === 0xffff
  ) {
    throw new Error("DOCX archive uses an unsupported ZIP structure");
  }
  if (totalEntries > MAX_ARCHIVE_ENTRIES) {
    throw new Error("DOCX archive contains too many entries");
  }
  if (
    recordOffset +
      ZIP_END_OF_CENTRAL_DIRECTORY_MIN_BYTES +
      commentBytes !==
      bytes.byteLength ||
    centralDirectoryOffset + centralDirectoryBytes !== recordOffset
  ) {
    throw new Error("Uploaded file has an invalid DOCX central directory");
  }

  const centralDirectoryEnd =
    centralDirectoryOffset + centralDirectoryBytes;
  let centralCursor = centralDirectoryOffset;
  let actualEntries = 0;
  let hasDocumentManifest = false;
  let totalDeclaredUncompressedBytes = 0;
  const decoder = new TextDecoder("utf-8", { fatal: false });

  while (centralCursor < centralDirectoryEnd) {
    if (
      centralCursor + ZIP_CENTRAL_DIRECTORY_HEADER_BYTES >
        centralDirectoryEnd ||
      view.getUint32(centralCursor, true) !==
        ZIP_CENTRAL_DIRECTORY_SIGNATURE
    ) {
      throw new Error("Uploaded file has an invalid DOCX central directory");
    }

    const compressedSize = view.getUint32(centralCursor + 20, true);
    const uncompressedSize = view.getUint32(centralCursor + 24, true);
    const fileNameBytes = view.getUint16(centralCursor + 28, true);
    const extraFieldBytes = view.getUint16(centralCursor + 30, true);
    const fileCommentBytes = view.getUint16(centralCursor + 32, true);

    const nameStart = centralCursor + ZIP_CENTRAL_DIRECTORY_HEADER_BYTES;
    const entryName = decoder.decode(
      bytes.subarray(nameStart, nameStart + fileNameBytes),
    );
    const isDocumentXml = /^word\/document\.xml$/i.test(entryName);

    if (isDocumentXml) {
      hasDocumentManifest = true;
    }

    totalDeclaredUncompressedBytes += uncompressedSize;
    if (totalDeclaredUncompressedBytes > MAX_ARCHIVE_DECLARED_UNCOMPRESSED_BYTES) {
      throw new Error("DOCX archive exceeds the total extraction limit");
    }

    if (isDocumentXml) {
      if (uncompressedSize > MAX_DOCUMENT_XML_BYTES) {
        throw new Error(`${entryName} exceeds the DOCX extraction limit`);
      }
      if (
        uncompressedSize > 0 &&
        (compressedSize === 0 ||
          uncompressedSize / compressedSize > MAX_COMPRESSION_RATIO)
      ) {
        throw new Error(`${entryName} has a suspicious compression ratio`);
      }
    }

    centralCursor +=
      ZIP_CENTRAL_DIRECTORY_HEADER_BYTES +
      fileNameBytes +
      extraFieldBytes +
      fileCommentBytes;
    actualEntries += 1;
    if (actualEntries > MAX_ARCHIVE_ENTRIES) {
      throw new Error("DOCX archive contains too many entries");
    }
  }
  if (centralCursor !== centralDirectoryEnd || actualEntries !== totalEntries) {
    throw new Error("Uploaded file has an invalid DOCX central directory");
  }
  if (!hasDocumentManifest) {
    throw new Error(
      "Uploaded file is not a valid DOCX archive (missing document manifest)",
    );
  }
  return { bytes, totalDeclaredUncompressedBytes };
}

// ---------------------------------------------------------------------------
// officeparser text-node walker (pptx.ts's walker plus explicit break nodes)
// ---------------------------------------------------------------------------

function collectNodeText(node: OfficeContentNode): string {
  if (node.type === "break") return "\n";
  const hasBreaks = node.children?.some((c) => c.type === "break");
  if (
    node.text &&
    !(node.type === "cell" && node.children?.length) &&
    !hasBreaks
  ) {
    return node.text;
  }
  if (!node.children?.length) return "";
  const parts: string[] = [];
  for (const child of node.children) {
    const childText = collectNodeText(child);
    if (childText) parts.push(childText);
  }
  return parts.join("\n");
}

// ---------------------------------------------------------------------------
// Main extraction
// ---------------------------------------------------------------------------

export async function extractDocxText(
  data: ArrayBuffer | Uint8Array,
): Promise<DocxTextExtraction> {
  const { bytes, totalDeclaredUncompressedBytes } = assertArchiveSafety(data);

  const { parseOffice } = await import("officeparser");

  const ast = await parseOffice(bytes, {
    fileType: "docx",
    includeBreakNodes: true,
    extractAttachments: false,
    ocr: false,
    includeRawContent: false,
    decompressionLimits: {
      maxUncompressedBytes:
        totalDeclaredUncompressedBytes + UNCOMPRESSED_BYTES_SLACK,
      maxZipEntries: MAX_ARCHIVE_ENTRIES,
    },
  });

  const contentTexts: string[] = [];
  let extractedCharacters = 0;

  for (const node of ast.content) {
    const nodeText = collectNodeText(node);
    if (nodeText) {
      contentTexts.push(nodeText);
      extractedCharacters += nodeText.length;
      if (extractedCharacters > MAX_EXTRACTED_TEXT_CHARS) {
        throw new Error("DOCX extracted text exceeds the synthesis limit");
      }
    }
  }

  const text = sanitizeDocxText(contentTexts.join("\n\n"));
  if (!text) {
    throw new Error(DOCX_NO_TEXT_ERROR);
  }

  return { text };
}

// ---------------------------------------------------------------------------
// Client-side File wrapper
// ---------------------------------------------------------------------------

/**
 * Parse a DOCX file to text using client-side extraction.
 * Throws if the file is not a DOCX, parsing fails, or no text is extracted.
 */
export async function parseDocxToText(file: File): Promise<string> {
  if (
    file.type !== DOCX_MIME_TYPE &&
    !file.name.toLowerCase().endsWith(".docx")
  ) {
    throw new Error("File must be a DOCX");
  }

  const buffer = await file.arrayBuffer();
  return (await extractDocxText(buffer)).text;
}
